/**
 * Bulk refresh prezzi su tutti i preventivi disallineati.
 *
 * Riceve una lista di estimate IDs (tipicamente da dashboard `pricesStaleEstimates`),
 * carica le stratigrafie di ognuno e le passa al bulkUpdate esistente.
 * Aggrega un report finale e mostra toast riassuntivo.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBulkUpdateEstimateStratigraphyPrices } from './useBulkUpdateEstimateStratigraphyPrices';
import type { EstimateStratigraphy } from '@/types/estimateStratigraphy';

/** Mappa una row DB a `EstimateStratigraphy` frontend (snake_case → camelCase). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dbToFrontend(row: any): EstimateStratigraphy {
  return {
    id: row.id,
    estimateId: row.estimate_id,
    stratigraphyId: row.stratigraphy_id ?? '',
    name: row.name,
    description: row.description ?? undefined,
    area: Number(row.area ?? 0),
    quantity: Number(row.quantity ?? 1),
    unitCost: Number(row.unit_cost ?? 0),
    totalCost: Number(row.total_cost ?? 0),
    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
    updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
    stratigraphyData: row.stratigraphy_data ?? undefined,
    layersData: row.layers_data ?? undefined,
    pricesUpdatedAt: row.prices_updated_at ? new Date(row.prices_updated_at) : undefined,
    isSnapshot: row.is_snapshot ?? undefined,
    originalStratigraphyId: row.original_stratigraphy_id ?? undefined,
    wallHeight: row.wall_height ?? undefined,
    finishLevel: row.finish_level ?? undefined,
    finishCostPerSqm: row.finish_cost_per_sqm ?? undefined,
    finishLaborMinutesPerSqm: row.finish_labor_minutes_per_sqm ?? undefined,
    finishComponentsData: row.finish_components_data ?? undefined,
    // Senza questo campo il ricalcolo lanciato dalla dashboard riscriveva
    // `total_cost` SENZA la maggiorazione per difficolta`, mentre lo stesso
    // ricalcolo lanciato dalla scheda preventivo la conservava: il bulk legge
    // la difficolta` dall'oggetto in memoria (non la rilegge dal DB come fa
    // invece per finitura e perimetro), quindi ometterla qui equivaleva a
    // dichiarare 1.00 su ogni riga.
    difficultyMultiplier: row.difficulty_multiplier != null ? Number(row.difficulty_multiplier) : undefined,
  };
}

interface RunSummary {
  totalEstimates: number;
  estimatesOk: number;
  estimatesFailed: number;
  rowsUpdated: number;     // righe con prezzo davvero cambiato (changed=true)
  rowsVerified: number;    // righe ricalcolate ma prezzo invariato (changed=false)
  rowsSkipped: number;     // snapshot orfani / no_valid_layers / zero_cost
  rowsFailed: number;
}

export const useRefreshAllStaleEstimates = () => {
  const queryClient = useQueryClient();
  const { bulkUpdatePricesAsync } = useBulkUpdateEstimateStratigraphyPrices();

  const mutation = useMutation<RunSummary, Error, { estimateIds: string[] }>({
    mutationFn: async ({ estimateIds }) => {
      const summary: RunSummary = {
        totalEstimates: estimateIds.length,
        estimatesOk: 0,
        estimatesFailed: 0,
        rowsUpdated: 0,
        rowsVerified: 0,
        rowsSkipped: 0,
        rowsFailed: 0,
      };

      for (const estimateId of estimateIds) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: rows, error } = await (supabase as any)
            .from('estimate_stratigraphies')
            .select('*')
            .eq('estimate_id', estimateId);
          if (error) throw error;
          if (!rows || rows.length === 0) continue;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const adapted: EstimateStratigraphy[] = (rows as any[]).map(dbToFrontend);
          const report = await bulkUpdatePricesAsync({
            estimateStratigraphies: adapted,
            silentToast: true,
          });
          summary.estimatesOk++;
          summary.rowsUpdated += report.updated.filter(u => u.changed).length;
          summary.rowsVerified += report.updated.filter(u => !u.changed).length;
          summary.rowsSkipped += report.skipped.length;
          summary.rowsFailed += report.failed.length;
        } catch (e) {
          console.error('[useRefreshAllStaleEstimates] estimate failed:', estimateId, e);
          summary.estimatesFailed++;
        }
      }

      return summary;
    },
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-estimates-comprehensive'] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });

      // Toast veritiero: distingue righe davvero modificate vs verificate (prezzo invariato)
      // vs skippate (orfani/originale eliminato/composizione corrotta). Evita falsi positivi
      // tipo "Aggiornati 17 (0 ricalcolate)" che confondevano l'utente.
      const parts: string[] = [];
      if (summary.rowsUpdated > 0) parts.push(`${summary.rowsUpdated} prezzi aggiornati`);
      if (summary.rowsVerified > 0) parts.push(`${summary.rowsVerified} prezzi invariati`);
      if (summary.rowsSkipped > 0) parts.push(`${summary.rowsSkipped} orfani/non ricalcolabili`);
      if (summary.rowsFailed > 0) parts.push(`${summary.rowsFailed} errori`);
      const body = parts.length > 0 ? parts.join(' · ') : 'nessuna stratigrafia da aggiornare';

      if (summary.estimatesFailed === 0 && summary.rowsUpdated > 0) {
        toast.success(`${summary.estimatesOk} preventivi: ${body}`);
      } else if (summary.estimatesFailed === 0) {
        toast.info(`${summary.estimatesOk} preventivi verificati: ${body}`);
      } else {
        toast.warning(
          `${summary.estimatesOk}/${summary.totalEstimates} preventivi · ${summary.estimatesFailed} falliti · ${body}`,
        );
      }
    },
    onError: (e) => {
      console.error('[useRefreshAllStaleEstimates] global error:', e);
      toast.error('Errore durante l\'aggiornamento bulk');
    },
  });

  return {
    refreshAll: mutation.mutate,
    refreshAllAsync: mutation.mutateAsync,
    isRefreshing: mutation.isPending,
  };
};
